import { pipe, catchError } from 'overmind';

import { isWebmapAvailable } from '@/common/overmind/namespaces/arcgis/config';
import type {
  CompletedCalculationTactic,
  DomainGuid
} from '@/features/execute-calculation-set/overmind/calculation';
import type { Context } from '@/overmind';
import type { LayerId } from '@/overmind/effects/adapters/baseadapter';
import { getRequestAborter } from '@/overmind/effects/requestAborter';
import { UnexpectedStateError, AbortError } from '@/overmind/errors';

import { isCalculationTacticResultUnavailable } from '../../calculation/actions';

const { switchToNewRequest, completeRequestOperator } =
  getRequestAborter('getFeatures');

export type GetFeaturesActionBody = {
  featureIds: DomainGuid[];
  layerIdsToSearch: LayerId[];
  gridVersionName: string | undefined;
};

export const getFeatures = pipe(
  async (
    { state, effects }: Context,
    { featureIds, layerIdsToSearch, gridVersionName }: GetFeaturesActionBody
  ) => {
    const abortController = switchToNewRequest();
    state.arcgis.featureGeometries.send('TRIGGER_LOADING');
    const adapter = effects.arcgisMapAdapter.getAdapter();
    const arcgisConfiguration = state.arcgis.config;
    const calculationSetExecution = state.calculation.calculationTactic;

    if (isCalculationTacticResultUnavailable(calculationSetExecution.value))
      throw new UnexpectedStateError();

    if (adapter) {
      if (
        shouldUseFeatureTitlesFromWebmap(calculationSetExecution.value) &&
        isWebmapAvailable(arcgisConfiguration)
      ) {
        const unloadedWebmap = effects.arcgisMapAdapter.createWebMap(
          arcgisConfiguration.value.map.webMap
        );
        const webMap = await unloadedWebmap.load();
        const getTitlesForFeatures =
          effects.arcgisMapAdapter.createTitleRetrievingFunction(webMap);
        const findLayerName = effects.arcgisMapAdapter.createFindLayerName(webMap);

        return await adapter.getFeaturesWithTitle(
          featureIds,
          layerIdsToSearch,
          getTitlesForFeatures,
          findLayerName,
          gridVersionName,
          abortController
        );
      }
      return adapter.getFeatures(
        featureIds,
        layerIdsToSearch,
        gridVersionName,
        abortController
      );
    }

    throw new UnexpectedStateError();

    function shouldUseFeatureTitlesFromWebmap(
      calculationSetExecution: CompletedCalculationTactic
    ) {
      return (
        calculationSetExecution.calculationSetResult.voltageLevel !==
        'MediumVoltage'
      );
    }
  },
  ({ state }: Context, features) => {
    state.arcgis.featureGeometries.send('FETCH_SUCCESS', features);
  },
  catchError(({ state }: Context, error) => {
    if (
      !(error instanceof UnexpectedStateError || error instanceof AbortError)
    ) {
      state.arcgis.featureGeometries.send('FETCH_ERROR', error.message);
    }
  }),
  completeRequestOperator()
);

export const resetFeatureGeometriesState = pipe(async ({ state }: Context) => {
  state.arcgis.featureGeometries.send('UNLOAD');
});

